import type { CitationsFormState, SearchContext } from "../types";
import { Examples } from "./Examples";
import { choice, FIELD_MONO, HELP, LABEL } from "./styles";

const EXAMPLES = [
  { id: "arx_1706.03762", label: "Transformers" },
  { id: "arx_2404.14367", label: "Preference FT" },
  { id: "10.1038/s41591-021-01312-x", label: "AI in medicine" },
];

const PER_SIDE_OPTIONS = [10, 15, 20, 30];

export const CitationsControls = ({
  form,
  context,
  onChange,
  onSubmit,
}: {
  form: CitationsFormState;
  /** Last search result set — its papers double as quick-picks. */
  context: SearchContext | null;
  onChange: (next: CitationsFormState) => void;
  onSubmit: () => void;
}) => {
  const contextPicks = (context?.papers ?? [])
    .filter((p) => p.document_id || p.doi)
    .slice(0, 5);

  return (
    <div className="space-y-5">
      <div>
        <label
          htmlFor="playground-citations-paper"
          className={LABEL}
        >
          Paper
        </label>
        <input
          id="playground-citations-paper"
          value={form.paperId}
          className={`${FIELD_MONO} mt-1`}
          onChange={(e) => onChange({ ...form, paperId: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
          }}
        />
        <p className={HELP}>
          Draws the paper's citation neighborhood from the live graph
          (2.1B edges): who cites it, what it cites, and every
          cross-citation among them. Click any node to re-center.
        </p>
      </div>

      <Examples
        items={EXAMPLES.map((example) => ({ value: example.id, label: example.label }))}
        onPick={(paperId) => onChange({ ...form, paperId })}
      />

      {contextPicks.length > 0 && (
        <div>
          <p className={LABEL}>
            From your last search
          </p>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-0.5">
            {contextPicks.map((paper) => {
              const id = paper.document_id || paper.doi || "";
              return (
                <button
                  key={id}
                  type="button"
                  title={paper.title}
                  onClick={() => onChange({ ...form, paperId: id })}
                  className="max-w-full truncate text-sm italic text-zinc-500 transition-colors hover:text-zinc-900"
                >
                  {(paper.title ?? id).slice(0, 42)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <fieldset>
        <legend className={LABEL}>
          Nodes per side
        </legend>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-4" role="group">
          {PER_SIDE_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={form.perSide === n}
              onClick={() => onChange({ ...form, perSide: n })}
              className={choice(form.perSide === n)}
            >
              {n}
            </button>
          ))}
        </div>
        <p className={HELP}>
          The most-cited citers and references are shown around the
          selected paper.
        </p>
      </fieldset>
    </div>
  );
};
